import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../api/axios';
import logo from '../wysupp-icon-green-transparent.svg';
import AccountInfo from '../AccountInfo';
import { FaArrowLeft, FaUser, FaEnvelope, FaUserTag, FaExclamationTriangle, FaTrash } from 'react-icons/fa';
import { useAuth } from '../AuthContext';

export default function EditUserFormPage() {
    const { user } = useAuth();
    const { userId } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({ username: '', email: '', role: '' });
    const [originalData, setOriginalData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await axiosInstance.get(`/api/users/${userId}`);
                const userData = response.data;
                const initialFormData = {
                    username: userData.username,
                    email: userData.email || '',
                    role: userData.role,
                };
                setFormData(initialFormData);
                setOriginalData(initialFormData);
                setIsActive(userData.is_active);
                setLoading(false); // Stop loading on success
            } catch (err) {
                // --- TIMEOUT FIX: Prevent red error flash on timeout ---
                if (err.response?.status === 401) return;

                setError(err.response?.data?.detail || 'Failed to fetch user data.');
                setLoading(false); // Stop loading on actual errors
            }
        };
        fetchUserData();
    }, [userId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleToggleActiveStatus = async () => {
        const actionText = isActive ? 'deactivate' : 'reactivate';
        if (!window.confirm(`Are you sure you want to ${actionText} this user?`)) return;

        setError('');
        setSuccess('');
        try {
            const response = await axiosInstance.patch(`/api/users/${userId}/status`, { is_active: !isActive });
            setIsActive(response.data.is_active);
            setSuccess(`User successfully ${actionText}d!`);
        } catch (err) {
            // --- TIMEOUT FIX: Play dead on 401 ---
            if (err.response?.status === 401) return;

            setError(err.response?.data?.detail || `Failed to ${actionText} user.`);
        }
    };

    const handleDeleteUser = async () => {
        if (!window.confirm('DANGER: Are you absolutely sure you want to permanently delete this user? This action cannot be undone.')) return;

        setError('');
        setSuccess('');
        try {
            await axiosInstance.delete(`/api/users/${userId}`);
            alert('User successfully deleted.');
            navigate('/admin/manage-users/edit-user');
        } catch (err) {
            // --- TIMEOUT FIX: Play dead on 401 ---
            if (err.response?.status === 401) return;

            setError(err.response?.data?.detail || 'Failed to delete user.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        const payload = {};
        if (formData.username !== originalData.username) payload.username = formData.username;
        if (formData.role !== originalData.role) payload.role = formData.role;
        if (formData.email !== originalData.email) payload.email = formData.email;

        if (Object.keys(payload).length === 0) {
            setSuccess("No changes were made.");
            return;
        }

        try {
            const response = await axiosInstance.put(`/api/users/${userId}`, payload);
            const updatedUserData = response.data;
            setSuccess("User updated successfully!");
            setOriginalData({
                username: updatedUserData.username,
                email: updatedUserData.email || '',
                role: updatedUserData.role
            });
            setIsActive(updatedUserData.is_active);
        } catch (err) {
            // --- TIMEOUT FIX: Play dead on 401 ---
            if (err.response?.status === 401) return;

            setError(err.response?.data?.detail || "Failed to update user.");
        }
    };

    if (loading) {
        return (
            <div className="admin-page-container">
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading user data...</p>
                </div>
                <style>{`
                    :root { --dark-bg: #1a202c; --text-color: #e2e8f0; --green-accent: #48bb78; --border-color: #4a5568; }
                    .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; display: flex; align-items: center; justify-content: center; }
                    .loading-state { text-align: center; color: var(--text-color); }
                    .loading-spinner { width: 40px; height: 40px; margin: 0 auto 1rem; border: 4px solid var(--border-color); border-top-color: var(--green-accent); border-radius: 50%; animation: spin 1s linear infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className="admin-page-container">
            <header className="dashboard-header">
                <Link to="/home" style={{ textDecoration: 'none' }}>
                    <div className="logo-section" style={{ cursor: 'pointer' }}>
                        <img src={logo} className="logo-icon" alt="Wysupp Logo" />
                        <span className="logo-text">Wysupp Validate</span>
                        <span className="logo-subtext">Data Quality Management</span>
                    </div>
                </Link>
                <AccountInfo />
            </header>

            <main className="admin-main-content">
                <div className="page-header">
                    <button onClick={() => navigate('/admin/manage-users/edit-user')} className="back-button">
                        <FaArrowLeft /> Back to User List
                    </button>
                </div>

                <div className="admin-form-wrapper">
                    <h1 className="form-title">Edit User: {originalData.username}</h1>
                    <p className="form-subtitle">Modify user details and manage their account status.</p>

                    <form className="admin-form" onSubmit={handleSubmit}>
                        {success && <div className="form-feedback success">{success}</div>}
                        {error && <div className="form-feedback error">{error}</div>}

                        <div className="input-group">
                            <label htmlFor="username">Username</label>
                            <div className="input-container">
                                <FaUser className="input-icon" />
                                <input type="text" id="username" name="username" value={formData.username} onChange={handleChange} required />
                            </div>
                        </div>

                        <div className="input-group">
                            <label htmlFor="email">Email</label>
                            <div className="input-container">
                                <FaEnvelope className="input-icon" />
                                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} />
                            </div>
                        </div>

                        <div className="input-group">
                            <label htmlFor="role">Role</label>
                            <div className="input-container">
                                <FaUserTag className="input-icon" />
                                <select id="role" name="role" value={formData.role} onChange={handleChange} required>
                                    <option value="viewer">Viewer</option>
                                    <option value="editor">Editor</option>
                                    <option value="manager">Manager</option>
                                    <option value="admin" disabled={user?.role !== 'admin'}>
                                        Admin {user?.role !== 'admin' && '(Creation restricted)'}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="action-button primary-button">
                                Save Changes
                            </button>
                        </div>
                    </form>

                    <div className="danger-zone">
                        <h2 className="danger-zone-title"><FaExclamationTriangle /> Danger Zone</h2>
                        <div className="danger-zone-actions">
                            <div className="danger-action">
                                <p><strong>{isActive ? 'Deactivate' : 'Reactivate'} User</strong><br />Temporarily suspend or restore user access.</p>
                                <button onClick={handleToggleActiveStatus} className="action-button deactivate-button">
                                    {isActive ? 'Deactivate' : 'Reactivate'}
                                </button>
                            </div>
                            <div className="danger-action">
                                <p><strong>Delete User</strong><br />Permanently remove this user and all associated data.</p>
                                <button onClick={handleDeleteUser} className="action-button delete-button"><FaTrash /> Delete</button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <style>{`
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                    --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1);
                    --success-color: #a7f3d0; --success-bg: rgba(52, 211, 153, 0.1);
                    --danger-red: #ef4444; --danger-red-hover: #dc2626;
                }
                .admin-page-container, .admin-page-container * {
                    box-sizing: border-box;
                }
                .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; color: var(--text-color); font-family: 'Inter', sans-serif; }
                .dashboard-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 3rem; background-color: var(--card-bg); border-bottom: 1px solid var(--border-color); }
                .logo-section { display: flex; align-items: center; gap: 0.5rem; }
                .logo-icon { width: 30px; height: 30px; }
                .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); }
                .admin-main-content { max-width: 800px; margin: 2rem auto; padding: 2rem; margin-bottom: 0rem; }
                .page-header { margin-bottom: 1.5rem; }
                .back-button { background: transparent; color: var(--light-text); border: none; padding: 0; font-weight: 600; cursor: pointer; transition: color 0.2s; display: flex; align-items: center; gap: 0.5rem; }
                .back-button:hover { color: var(--text-color); }
                
                .admin-form-wrapper { background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 12px; padding: 2.5rem; }
                .form-title { font-size: 1.75rem; font-weight: 700; color: var(--text-color); margin-bottom: 0.5rem; }
                .form-subtitle { font-size: 1rem; color: var(--light-text); margin-bottom: 2rem; }
                .admin-form { width: 100%; display: flex; flex-direction: column; gap: 1.5rem; }
                .form-feedback { width: 100%; padding: 0.75rem; border-radius: 6px; font-size: 0.9rem; font-weight: 500; margin-bottom: 1.5rem; }
                .form-feedback.success { background-color: var(--success-bg); color: var(--success-color); }
                .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); }
                
                .input-group { display: flex; flex-direction: column; }
                .input-group label { font-size: 0.875rem; font-weight: 500; color: var(--light-text); margin-bottom: 0.5rem; }
                .input-container { position: relative; }
                .input-container input, .input-container select {
                    width: 100%; padding: 0.75rem 1rem 0.75rem 2.5rem; background-color: var(--input-bg);
                    border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-color); font-size: 1rem;
                }
                .input-icon { position: absolute; top: 50%; left: 0.75rem; transform: translateY(-50%); color: var(--light-text); }
                .input-container select { appearance: none; background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23a0aec0%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E'); background-repeat: no-repeat; background-position: right 1rem center; background-size: 0.65em auto; }

                .form-actions { display: flex; justify-content: flex-end; margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px solid var(--border-color); }
                .action-button { padding: 0.6rem 1.2rem; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 0.5rem; }
                .primary-button { background-color: var(--green-accent); color: var(--dark-bg); }
                .primary-button:hover { background-color: #38a169; }

                .danger-zone { border: 1px solid #b91c1c; border-radius: 8px; margin-top: 2.5rem; padding: 1.5rem; }
                .danger-zone-title { font-size: 1.1rem; font-weight: 600; color: #fca5a5; display: flex; align-items: center; gap: 0.5rem; margin: 0 0 1.5rem 0; }
                .danger-zone-actions { display: flex; flex-direction: column; gap: 1.5rem; }
                .danger-action { display: flex; justify-content: space-between; align-items: center; gap: 1rem; }
                .danger-action p { margin: 0; color: var(--light-text); font-size: 0.9rem; }
                .danger-action p strong { color: var(--text-color); }
                .deactivate-button { background-color: #fef2f2; color: #b91c1c; border: 1px solid #fca5a5; }
                .deactivate-button:hover { background-color: #fee2e2; }
                .delete-button { background-color: var(--danger-red); color: white; }
                .delete-button:hover { background-color: var(--danger-red-hover); }
                .logo-subtext {
                    font-size: 0.75rem;
                    color: var(--light-text);
                    margin-left: 0.5rem;
                    padding-left: 0.5rem;
                     border-left: 1px solid var(--light-text);
                }
            `}</style>
        </div>
    );
}